﻿<template>
  <div ref="pageRoot" class="iphone-guide-page">
    <div class="hero-card">
      <div class="hero-top">
        <span class="hero-badge">两步完成</span>
        <button class="soft-button" @click="goToAuthPage">更换卡密</button>
      </div>

      <h1>苹果定位修改</h1>
      <p class="hero-desc">下载配置后连接 VPN，再进入系统。</p>

      <div class="session-strip">
        <span class="session-strip__item">当前卡密：{{ cardKey }}</span>
        <span class="session-strip__dot"></span>
        <span class="session-strip__item">剩余：{{ remainingValidityText }}</span>
      </div>
    </div>

    <div class="actions-card">
      <div class="system-entry-card system-entry-card--download">
        <div class="system-entry-card__label">第一步</div>
        <div class="system-entry-card__title">先下载并安装配置文件</div>
        <div class="system-entry-card__desc">首次使用请先下载配置文件，完成安装并按教程信任证书、连接 VPN。</div>
        <button class="primary-button action-button" :disabled="isDownloading || isCheckingSystem" @click="downloadVpnConfig">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="button-icon download-icon">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          <span>{{ isDownloading ? '下载中...' : '下载配置文件' }}</span>
        </button>
        <div class="system-entry-card__hint">安装完成后，再继续下面的进入系统。</div>
      </div>

      <div class="system-entry-card">
        <div class="system-entry-card__label">第二步</div>
        <div class="system-entry-card__title">连接 VPN 后，从这里进入系统</div>
        <div class="system-entry-card__desc">如果已经安装描述文件并开启 VPN，直接点击下面按钮进入地图页面。</div>
        <button class="enter-button action-button" :disabled="isCheckingSystem" @click="enterSystem">
          <svg t="1783481660177" class="button-icon custom-icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2246"><path d="M512 512m-426.666667 0a426.666667 426.666667 0 1 0 853.333334 0 426.666667 426.666667 0 1 0-853.333334 0Z" fill="#FFFFFF" fill-opacity="0.22" p-id="2247"></path><path d="M691.2 524.8c-12.8 14.933333-34.133333 17.066667-51.2 6.4l-247.466667-196.266667c-14.933333-12.8-17.066667-34.133333-6.4-49.066666 12.8-14.933333 34.133333-17.066667 51.2-6.4l247.466667 196.266666c14.933333 12.8 19.2 34.133333 6.4 49.066667z" fill="#FFFFFF" p-id="2248"></path><path d="M386.133333 738.133333c12.8 14.933333 34.133333 17.066667 51.2 6.4l247.466667-196.266666c14.933333-12.8 17.066667-34.133333 6.4-49.066667-12.8-14.933333-34.133333-17.066667-51.2-6.4l-247.466667 196.266667c-14.933333 12.8-19.2 34.133333-6.4 49.066666z" fill="#FFFFFF" p-id="2249"></path></svg>
          <span>{{ isCheckingSystem ? '检查中...' : '进入系统' }}</span>
        </button>
        <div class="system-entry-card__hint">如果无法进入，通常是 VPN 未连接。</div>
      </div>
      <button class="secondary-button action-button" :disabled="isCheckingSystem" @click="openTutorialPage">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="button-icon guide-icon">
          <circle cx="12" cy="12" r="10"/>
          <path d="M12 16v-4"/>
          <path d="M12 8h.01"/>
        </svg>
        <span>使用教程</span>
      </button>
    </div>
  </div>
</template>

<script>
import { Toast } from 'vant'
import { getCardSession } from './card-session'
import { verifyVpnConnection, vpnAccessErrorMessage } from './vpn-access'

export default {
  name: 'IphonLocal',
  data() {
    return {
      isDownloading: false,
      isCheckingSystem: false,
      cardKey: '',
      deviceId: '',
      cardInfo: {},
      expiresAt: 0,
      lastTouchEndAt: 0,
    }
  },
  computed: {
    cardEffectiveEndAt() {
      const effectiveEndTime = this.cardInfo && this.cardInfo.effectiveEndTime
      if (!effectiveEndTime) {
        return 0
      }
      const normalized = String(effectiveEndTime).replace(/-/g, '/')
      const timestamp = new Date(normalized).getTime()
      return Number.isNaN(timestamp) ? 0 : timestamp
    },
    remainingValidityText() {
      if (!this.cardEffectiveEndAt) {
        return '未知'
      }
      const diff = this.cardEffectiveEndAt - Date.now()
      if (diff <= 0) {
        return '已到期'
      }
      const totalMinutes = Math.ceil(diff / 60000)
      if (totalMinutes < 60) {
        return `${totalMinutes} 分钟`
      }
      const totalHours = Math.ceil(totalMinutes / 60)
      if (totalHours < 24) {
        return `${totalHours} 小时`
      }
      const totalDays = Math.ceil(totalHours / 24)
      return `${totalDays} 天`
    },
  },
  mounted() {
    this.restoreSession()
    if (this.$refs.pageRoot) {
      this.$refs.pageRoot.addEventListener('touchend', this.handleTouchEnd, { passive: false })
    }
  },
  beforeDestroy() {
    if (this.$refs.pageRoot) {
      this.$refs.pageRoot.removeEventListener('touchend', this.handleTouchEnd)
    }
  },
  methods: {
    handleTouchEnd(event) {
      if (!event || !event.changedTouches || event.changedTouches.length !== 1) {
        return
      }
      const now = Date.now()
      if (now - this.lastTouchEndAt < 300) {
        event.preventDefault()
      }
      this.lastTouchEndAt = now
    },
    restoreSession() {
      const session = getCardSession()
      if (!session) {
        this.$router.replace('/iphon-seting/auth')
        return
      }
      this.cardKey = session.cardKey
      this.deviceId = session.deviceId
      this.cardInfo = session.cardInfo || {}
      this.expiresAt = session.expiresAt
    },
    goToAuthPage() {
      this.$router.push('/iphon-seting/auth')
    },
    openTutorialPage() {
      this.$router.push('/iphon-seting/tutorial')
    },
    async checkVpnConnection() {
      const session = getCardSession()
      return verifyVpnConnection(session)
    },
    async downloadVpnConfig() {
      const session = getCardSession()
      if (!session) {
        Toast.fail('卡密已过期，请重新输入')
        this.$router.replace('/iphon-seting/auth')
        return
      }
      this.isDownloading = true
      try {
        // 凭据版本不变时代理密码保持稳定；时间戳仅用于绕过 Safari 和中间代理缓存。
        const url = `/api/vpn/card/download?cardKey=${encodeURIComponent(session.cardKey)}&deviceId=${encodeURIComponent(session.deviceId)}&_t=${Date.now()}`
        window.location.href = url
        Toast.success('下载已开始，请前往设置安装描述文件')
      } catch (error) {
        console.error('downloadVpnConfig error:', error)
        Toast.fail(error.message || '下载失败，请稍后重试')
      } finally {
        setTimeout(() => {
          this.isDownloading = false
        }, 1500)
      }
    },
    async enterSystem() {
      const session = getCardSession()
      if (!session) {
        Toast.fail('卡密已过期，请重新输入')
        this.$router.replace('/iphon-seting/auth')
        return
      }
      this.isCheckingSystem = true
      const loadingToast = Toast.loading({
        duration: 0,
        forbidClick: true,
        message: '正在检查 VPN 连接...',
      })
      try {
        await this.checkVpnConnection()
        loadingToast.clear()
        this.$router.push('/iphon-seting/map')
      } catch (error) {
        console.error('checkVpnConnection error:', error)
        loadingToast.clear()
        Toast.fail(vpnAccessErrorMessage(error))
      } finally {
        this.isCheckingSystem = false
      }
    },
  },
}
</script>

<style scoped>
.iphone-guide-page {
  min-height: 100vh;
  padding: 12px 12px 20px;
  background:
    radial-gradient(circle at top left, rgba(143, 211, 255, 0.22), transparent 34%),
    radial-gradient(circle at top right, rgba(190, 232, 255, 0.24), transparent 30%),
    linear-gradient(180deg, #f4f9ff 0%, #f8fbff 44%, #f1f6fc 100%);
  color: #0f172a;
  touch-action: manipulation;
  -webkit-text-size-adjust: 100%;
}

.hero-card,
.actions-card {
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(255, 255, 255, 0.82);
  box-shadow: 0 18px 40px rgba(87, 122, 163, 0.12);
  backdrop-filter: blur(14px);
  border-radius: 24px;
}

.hero-card {
  padding: 14px 14px 12px;
}

.hero-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  background: #eaf4ff;
  color: #3b82f6;
  font-size: 11px;
  font-weight: 600;
}

.hero-card h1 {
  margin: 0 0 6px;
  font-size: 22px;
  line-height: 1.15;
  letter-spacing: -0.03em;
}

.hero-desc {
  margin: 0;
  color: #526377;
  font-size: 12px;
  line-height: 1.5;
}

.session-strip {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f6fbff, #eef6ff);
  color: #47607b;
  font-size: 12px;
  overflow: hidden;
}

.session-strip__item {
  min-width: 0;
  flex: 1 1 auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.session-strip__dot {
  width: 4px;
  height: 4px;
  flex: 0 0 4px;
  border-radius: 50%;
  background: rgba(71, 96, 123, 0.4);
}

.actions-card {
  display: grid;
  gap: 10px;
  margin-top: 8px;
  padding: 14px;
}

.system-entry-card {
  padding: 16px;
  border-radius: 20px;
  background: linear-gradient(135deg, #eef7ff 0%, #e4f2ff 55%, #d8efff 100%);
  border: 1px solid rgba(129, 185, 235, 0.28);
  color: #12324f;
  box-shadow: 0 14px 28px rgba(87, 122, 163, 0.14);
}

.system-entry-card--download {
  background: linear-gradient(135deg, #f8fbff 0%, #eef6ff 55%, #e7f2ff 100%);
}

.system-entry-card__label {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.system-entry-card__title {
  margin-top: 10px;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.25;
}

.system-entry-card__desc,
.system-entry-card__hint {
  margin-top: 6px;
  color: #5d748d;
  font-size: 12px;
  line-height: 1.6;
}

.system-entry-card__hint {
  margin-top: 8px;
}

.primary-button,
.secondary-button,
.soft-button,
.enter-button {
  border: none;
  cursor: pointer;
  transition: opacity 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
  touch-action: manipulation;
}

.primary-button,
.secondary-button,
.enter-button {
  width: 100%;
  min-height: 46px;
  border-radius: 15px;
  font-size: 14px;
  font-weight: 700;
}

.enter-button {
  min-height: 52px;
  margin-top: 14px;
  border-radius: 16px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: #ffffff;
  box-shadow: 0 12px 24px rgba(59, 130, 246, 0.24);
  animation: enter-button-glow 2.4s ease-in-out infinite;
}

.primary-button {
  background: #f3f9ff;
  color: #2b5f95;
  border: 1px solid rgba(129, 185, 235, 0.42);
  box-shadow: none;
}

.action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.secondary-button {
  background: #f4f8fc;
  color: #35516f;
}

.primary-button:disabled,
.secondary-button:disabled,
.enter-button:disabled {
  opacity: 0.55;
  box-shadow: none;
  cursor: not-allowed;
  animation: none;
}

.soft-button {
  padding: 7px 12px;
  border-radius: 999px;
  background: #f3f8fd;
  color: #49627b;
  font-size: 12px;
}

.button-icon {
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.custom-icon {
  overflow: visible;
}

@keyframes enter-button-glow {
  0%,
  100% {
    transform: translateY(0);
    box-shadow: 0 12px 24px rgba(59, 130, 246, 0.24);
  }
  50% {
    transform: translateY(-1px);
    box-shadow: 0 14px 28px rgba(59, 130, 246, 0.3);
  }
}

@media (max-width: 480px) {
  .iphone-guide-page {
    padding: 10px 10px 16px;
  }

  .hero-card h1 {
    font-size: 20px;
  }

  .system-entry-card__title {
    font-size: 18px;
  }
}
</style>
